import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { animated, useSpring } from 'react-spring';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { puedeAbrirse } from '#components/mobile/budget/recuerdos';
import type {
  ObjetivoConFecha,
  Recuerdo,
} from '#components/mobile/budget/recuerdos';
import {
  aspectoBotella,
  aspectoDeRecuerdo,
} from '#components/mobile/budget/recuerdosAspecto';
import { idDispositivo } from '#components/mobile/budget/useRecuerdos';
import { Boton } from '#components/mobile/ui/Boton';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { estiloTarjeta } from '#components/mobile/ui/Tarjeta';
import {
  color,
  espacio,
  num,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';
import { useReducedMotion } from '#hooks/useReducedMotion';

import { Botella } from './Botella';
import { claseEscena, e, trazoOla } from './escenaCss';
import { GaviTinta } from './GaviTinta';
import { RUTA_BITACORA, useLlegadas } from './ProveedorLlegadas';
import { avance, BOYAS, boyasPasadas, mesEstimado, porApartar } from './ritmo';
import { useNoche } from './useNoche';

/** Alto de la escena de la ficha y de la botella dentro. */
const ALTO_ESCENA = 168;
const ALTO_BOTELLA = 92;
/** Línea del agua (desde arriba). */
const AGUA = 104;

type BotellaFichaProps = {
  categoriaId: string;
  nombre: string;
  objetivo: ObjetivoConFecha;
  saldo: IntegerAmount;
  /** Asignado este mes. */
  asignado: IntegerAmount;
  /** Cuota del mes (la de la meta). */
  cuota: IntegerAmount | null;
  /** Lo asignado en los meses anteriores (del más antiguo al último). */
  asignadosPrevios: readonly IntegerAmount[];
  month: string;
  /** A la derecha del título (marca «Suficiente» y estado de la meta). */
  derecha?: ReactNode;
  /** Debajo de todo: la meta editable (la tarjeta «Travesía» es una sola). */
  pie?: ReactNode;
};

type Estado =
  | { tipo: 'navegando' }
  | { tipo: 'en-muelle' }
  | { tipo: 'por-abrir'; recuerdo: Recuerdo }
  | { tipo: 'cerrada-hasta'; fecha: string }
  | { tipo: 'abierta'; recuerdo: Recuerdo };

/**
 * La botella de una travesía en la ficha de su categoría (visible mientras
 * ahorras): flota con un cabeceo suave entre el horizonte y el muelle según
 * el avance, pasa las boyas del 25/50/75 % y debajo cuenta lo que llevas,
 * a qué ritmo llegas (informativo, nunca «con retraso») y lo que te toca
 * apartar este mes. Gavi solo lo recuerda: no aparta nada sola.
 */
export function BotellaFicha({
  categoriaId,
  nombre,
  objetivo,
  saldo,
  asignado,
  cuota,
  asignadosPrevios,
  month,
  derecha,
  pie,
}: BotellaFichaProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const reducido = useReducedMotion();
  const noche = useNoche();
  const [soloLectura] = useMetadataPref('rumboSoloLectura');
  const { travesias, abrir } = useLlegadas();
  const hoy = monthUtils.currentDay();
  const disp = idDispositivo();

  const travesia = travesias.get(categoriaId);
  const recuerdo = travesia?.recuerdo ?? null;
  const meta = objetivo.importe;
  const progreso = avance(saldo, meta);
  const estado = estadoBotella(
    recuerdo,
    travesia?.resultado.tipo === 'en-muelle',
    disp,
    hoy,
  );
  const enMuelle = estado.tipo !== 'navegando';
  const posicion = enMuelle ? 1 : progreso;

  const aspecto = recuerdo
    ? aspectoDeRecuerdo(recuerdo, hoy)
    : aspectoBotella({
        clase: 'otra',
        importe: meta,
        zarpoEn: hoy,
        hasta: hoy,
        aportaciones: 0,
        largoCarta: 0,
        nombre,
      });
  const anchoBotella = (ALTO_BOTELLA * 120) / (aspecto.medidas.alto + 6);

  // Del horizonte (derecha) al muelle (izquierda): solo transform.
  const { x } = useSpring({
    x: 1 - posicion,
    immediate: reducido,
    config: { tension: 120, friction: 16 },
  });

  const fmt = (v: IntegerAmount) => format(v, 'financial');
  const pasadas = boyasPasadas(progreso);
  const estimado =
    estado.tipo === 'navegando' && month === monthUtils.currentMonth()
      ? mesEstimado({
          saldo,
          meta,
          asignados: [...asignadosPrevios, asignado],
          mes: month,
        })
      : null;
  const falta =
    estado.tipo === 'navegando' && month >= monthUtils.currentMonth()
      ? porApartar(cuota, asignado)
      : 0;
  const siEntonces = recuerdo?.base.siEntonces ?? '';
  const llegadaPrevista = monthUtils.format(
    objetivo.fecha,
    'd MMM yyyy',
    locale,
  );

  return (
    <View
      data-testid="botella-ficha"
      data-estado={estado.tipo}
      style={{
        ...estiloTarjeta('normal', 0),
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <div
        className={[claseEscena, noche ? 'noche' : '', reducido ? 'quieta' : '']
          .filter(Boolean)
          .join(' ')}
        style={{
          position: 'relative',
          height: ALTO_ESCENA,
          overflow: 'hidden',
        }}
        aria-hidden
      >
        <div
          className="e-full"
          style={{
            background: `linear-gradient(${e.cieloA}, ${e.cieloB} ${AGUA}px)`,
          }}
        />
        <svg
          className="e-ab"
          style={{ right: 0, top: AGUA - 41 }}
          width="110"
          height="47"
          viewBox="262 26 150 64"
          aria-hidden
        >
          <path
            d="M262 82 C290 64 318 50 346 44 C370 42 392 52 412 58 V90 H262Z"
            fill={e.costa}
          />
          <path d="M347 44 l2-12h4l2 12z M348 31h6v-3h-6z" fill={e.costa} />
          <circle
            cx="351"
            cy="29.5"
            r="2.4"
            fill="#f2d48a"
            style={{ opacity: e.faro }}
          />
        </svg>
        <div
          className="e-ab"
          style={{
            left: 0,
            right: 0,
            top: AGUA,
            bottom: 0,
            background: `linear-gradient(${e.marA}, ${e.marB} 120px)`,
          }}
        />
        <div
          className="e-ab e-ola e-ambiente"
          style={{ left: 0, top: AGUA + 8, width: 824 }}
        >
          <svg width="824" height="80" aria-hidden>
            <path
              d={trazoOla(8, 4)}
              fill={e.marC}
              stroke={e.cresta}
              strokeWidth="1"
            />
          </svg>
        </div>

        {/* Boyas del 25/50/75 % sobre la línea de la travesía. */}
        <div
          className="e-ab"
          style={{ left: '22%', right: '10%', top: AGUA - 4, height: 20 }}
        >
          {BOYAS.map((b, i) => (
            <Boya
              key={b}
              izquierda={`${(1 - b) * 100}%`}
              pasada={i < pasadas}
            />
          ))}
        </div>

        {/* La botella: recorre la pista con un muelle (react-spring). */}
        <div
          className="e-ab"
          style={{
            left: '22%',
            right: '10%',
            top: AGUA + 18 - ALTO_BOTELLA,
            height: ALTO_BOTELLA,
          }}
        >
          <animated.div
            data-testid="botella-pista"
            data-progreso={posicion.toFixed(3)}
            style={{
              position: 'absolute',
              inset: 0,
              transform: x.to(v => `translateX(${(v * 100).toFixed(2)}%)`),
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: -anchoBotella / 2,
                top: 0,
                width: anchoBotella,
                height: ALTO_BOTELLA,
              }}
            >
              <div
                className="e-cabeceo e-ambiente"
                style={{ position: 'absolute', inset: 0 }}
              >
                <div
                  className="e-vaiven e-ambiente"
                  style={{ position: 'absolute', inset: 0 }}
                >
                  <Botella
                    aspecto={aspecto}
                    abierta={estado.tipo === 'abierta'}
                    ancho={anchoBotella}
                  />
                </div>
              </div>
            </div>
          </animated.div>
        </div>

        <div
          className="e-ab e-ola rapida e-ambiente"
          style={{ left: 0, top: AGUA + 14, width: 824 }}
        >
          <svg width="824" height="70" aria-hidden>
            <path
              d={trazoOla(10, 5)}
              fill={e.marD}
              fillOpacity=".86"
              stroke={e.cresta}
              strokeWidth="1"
            />
          </svg>
        </div>

        {/* Muelle con Gavi a tinta en el noray. */}
        <svg
          className="e-ab"
          style={{ left: -6, top: AGUA - 6 }}
          width="72"
          height="70"
          viewBox="0 0 96 80"
          aria-hidden
        >
          <path d="M0 34h86v6H0z" fill={e.muelle} />
          <path
            d="M10 40h5v40h-5zM48 40h5v40h-5zM78 40h5v40h-5z"
            fill={e.muelle}
          />
          <path
            d="M40 34v-9c0-3 3-4 6-4s6 1 6 4v9z M37 22h18v-3H37z"
            fill={e.muelle}
          />
        </svg>
        <GaviTinta
          ancho={30}
          style={{ position: 'absolute', left: 14, top: AGUA - 22 }}
        />
      </div>

      <View
        style={{
          padding: `${espacio.fila + 4}px ${espacio.margen}px ${espacio.margen}px`,
          gap: 6,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            minHeight: TACTIL,
            margin: '-6px 0 0',
          }}
        >
          <IconoCaja icono="velero" tono="ok" size={32} />
          <Text style={{ ...texto.titulo }} data-testid="botella-titulo">
            <Trans>Voyage</Trans>
          </Text>
          <View style={{ flex: 1 }} />
          {derecha ?? (
            <Text style={{ fontSize: 12, color: color.fg3, ...num }}>
              {t('Due {{date}}', { date: llegadaPrevista })}
            </Text>
          )}
        </View>

        {estado.tipo === 'navegando' && (
          <>
            <PrivacyFilter>
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: color.fg,
                  ...num,
                }}
                data-testid="botella-llevas"
              >
                {t('So far {{saved}} of {{goal}}', {
                  saved: fmt(Math.max(0, saldo)),
                  goal: fmt(meta),
                })}
              </Text>
            </PrivacyFilter>
            {pasadas > 0 && (
              <Text style={{ fontSize: 13, color: color.fg2 }}>
                {textoBoya(t, pasadas)}
              </Text>
            )}
            {estimado && (
              <Text
                style={{ fontSize: 13, color: color.fg2 }}
                data-testid="botella-ritmo"
              >
                {t('At this pace you arrive in {{month}}', {
                  month: monthUtils.format(estimado, 'MMMM yyyy', locale),
                })}
              </Text>
            )}
          </>
        )}

        {estado.tipo === 'en-muelle' && (
          <Text style={{ fontSize: 14, color: color.fg2 }}>
            <Trans>
              It has reached the dock. Whoever sails this boat will open it.
            </Trans>
          </Text>
        )}

        {estado.tipo === 'cerrada-hasta' && (
          <Text style={{ fontSize: 14, color: color.fg2 }}>
            {t('It has arrived. It opens on {{date}}.', {
              date: monthUtils.format(estado.fecha, 'd MMMM yyyy', locale),
            })}
          </Text>
        )}

        {estado.tipo === 'por-abrir' && (
          <View style={{ gap: 10 }}>
            <Text style={{ fontSize: 16, fontWeight: 800, color: color.fg }}>
              <Trans>It has arrived.</Trans>
            </Text>
            <Boton
              bloque
              data-testid="botella-abrir"
              onPress={() => abrir(estado.recuerdo.base.rid)}
            >
              <Trans>Open the bottle</Trans>
            </Boton>
          </View>
        )}

        {estado.tipo === 'abierta' && (
          <View style={{ gap: 10 }}>
            <Text style={{ fontSize: 14, color: color.fg2 }}>
              {t('Arrived on {{date}}. It is in your logbook.', {
                date: monthUtils.format(
                  estado.recuerdo.llegada?.fecha ?? hoy,
                  'd MMMM yyyy',
                  locale,
                ),
              })}
            </Text>
            <Boton
              variante="fantasma"
              data-testid="botella-bitacora"
              onPress={() =>
                void navigate(
                  `${RUTA_BITACORA}?rid=${encodeURIComponent(estado.recuerdo.base.rid)}`,
                )
              }
            >
              <Trans>Open your logbook</Trans>
            </Boton>
          </View>
        )}

        {siEntonces && estado.tipo === 'navegando' && (
          <Text
            style={{
              fontSize: 13,
              color: color.fg2,
              fontStyle: 'italic',
              overflowWrap: 'anywhere',
            }}
            data-testid="botella-si-entonces"
          >
            {siEntonces}
          </Text>
        )}

        {falta > 0 && !soloLectura && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              marginTop: 2,
            }}
            data-testid="botella-cuota"
          >
            <div
              className={claseEscena}
              style={{ flexShrink: 0, lineHeight: 0 }}
            >
              <GaviTinta ancho={22} />
            </div>
            <Text style={{ fontSize: 13, color: color.fg, flex: 1 }}>
              {t('This month you have {{amount}} to set aside', {
                amount: fmt(falta),
              })}
            </Text>
          </View>
        )}
        {pie}
      </View>
    </View>
  );
}

function Boya({ izquierda, pasada }: { izquierda: string; pasada: boolean }) {
  return (
    <svg
      width="12"
      height="18"
      viewBox="0 0 12 18"
      data-boya={pasada ? 'pasada' : 'por-pasar'}
      style={{ position: 'absolute', left: izquierda, top: 0, marginLeft: -6 }}
      aria-hidden
    >
      <path d="M6 1v5" stroke={e.laton} strokeWidth="1.2" />
      <path
        d="M2 15 4 6h4l2 9Z"
        fill={pasada ? e.laton : 'none'}
        stroke={e.laton}
        strokeWidth="1.2"
      />
      <path d="M1 15h10" stroke={e.cresta} strokeWidth="1" />
    </svg>
  );
}

function textoBoya(t: (k: string) => string, pasadas: number): string {
  if (pasadas >= 3) {
    return t('Three quarters of the voyage');
  }
  return pasadas === 2
    ? t('Halfway through the voyage')
    : t('A quarter of the voyage');
}

/** En qué punto está la botella para este dispositivo. */
export function estadoBotella(
  recuerdo: Recuerdo | null,
  enMuelleSoloVer: boolean,
  disp: string,
  hoy: string,
): Estado {
  if (recuerdo?.llegada) {
    if (!puedeAbrirse(recuerdo, hoy)) {
      return { tipo: 'cerrada-hasta', fecha: recuerdo.base.abrirDesde ?? hoy };
    }
    return recuerdo.aportes[disp]?.abiertaEn
      ? { tipo: 'abierta', recuerdo }
      : { tipo: 'por-abrir', recuerdo };
  }
  return enMuelleSoloVer ? { tipo: 'en-muelle' } : { tipo: 'navegando' };
}
